import { FC, memo, useCallback } from 'react'
import { Typography, Box } from '@mui/material'
import dayjs from 'dayjs'
import type {
  FilterFieldConfig,
  FilterState,
  DateRange,
  UpdateDateRangeParams,
} from '@/utilities/types/shared-filter'
import { DateRangeSelector } from './DateRangeSelector'
import { SelectFilter } from './SelectFilter'

interface FilterFieldProps {
  config: FilterFieldConfig
  filters: FilterState
  onFilterChange: (field: string, value: any) => void
  onDateRangeChange: (params: UpdateDateRangeParams) => void
  tab?: string
}

const isValidDateRange = (
  start: string | null,
  end: string | null,
): boolean => {
  if (!start || !end) return true
  return dayjs(start).isBefore(dayjs(end)) || dayjs(start).isSame(dayjs(end))
}

export const FilterField: FC<FilterFieldProps> = memo(
  ({ config, filters, onFilterChange, onDateRangeChange, tab }) => {
    const { type, field, label, options = [] } = config

    const handleDateRangeChange = useCallback(
      (params: UpdateDateRangeParams) => {
        const { range } = params
        if (!isValidDateRange(range.start, range.end)) {
          console.warn(
            'Invalid date range: end date cannot be before start date',
          )
          return
        }
        onDateRangeChange(params)
      },
      [onDateRangeChange],
    )

    const handleFilterChange = useCallback(
      (field: string, selectedValues: string[]) => {
        if (field === 'region') {
          // Pass the exact match filters
          onFilterChange('regionName', selectedValues)
        } else {
          onFilterChange(field, selectedValues)
        }
      },
      [onFilterChange],
    )

    const renderField = () => {
      switch (type) {
        case 'date':
          return (
            <DateRangeSelector
              field={field}
              label={label}
              value={filters.dateRanges[field] || { start: null, end: null }}
              onChange={handleDateRangeChange}
              validation={{
                isValidRange: isValidDateRange,
                errorMessage: 'End date cannot be before start date',
              }}
            />
          )
        case 'select':
          const currentValue =
            field === 'region'
              ? filters.regularFilters['regionName'] || []
              : filters.regularFilters[field] || []

          return (
            <SelectFilter
              field={field}
              label={label}
              value={currentValue}
              options={options}
              onChange={handleFilterChange}
              isMulti={config.isMulti}
              tab={tab}
            />
          )
        default:
          return null
      }
    }

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
        {renderField()}
      </Box>
    )
  },
)

FilterField.displayName = 'FilterField'
